import { Controller } from '@hotwired/stimulus'

// TemplateEditorController: manages per-language reminder template editing state (subject/front) client-side.
export default class extends Controller {
  static targets = ['languageSelect', 'subjects', 'bodies', 'subjectInput', 'bodyEditor']

  declare readonly languageSelectTarget: HTMLSelectElement
  declare readonly subjectsTarget: HTMLElement
  declare readonly bodiesTarget: HTMLElement
  declare readonly hasLanguageSelectTarget: boolean
  declare readonly hasSubjectsTarget: boolean
  declare readonly hasBodiesTarget: boolean
  declare readonly subjectInputTargets: HTMLInputElement[]
  declare readonly bodyEditorTargets: (HTMLElement | HTMLTextAreaElement)[]

  // Map<locale, { subject: string, front: string }>
  private state: Map<string, { subject: string, front: string }> = new Map()
  private currentLocale: string | null = null
  private dateInput: HTMLInputElement | null = null
  private lastKnownTime: string | null = null

  connect(): void {
    this.initDatePicker()
    this.initDateChangeHandler()
    if (this.hasLanguageSelectTarget && this.hasSubjectsTarget && this.hasBodiesTarget) {
      this.initializeState()
      this.change()
      return
    }
    // Single-language: still capture default locale state for submit reliability
    this.initializeState()
  }

  initDatePicker(): void {
    const tryInit = (attempts = 0) => {
      const hasEB = (window as any).EB && (window as any).EB.Datepicker && typeof (window as any).EB.Datepicker.initDatePickers === 'function'
      if (hasEB) {
        (window as any).EB.Datepicker.initDatePickers(new Date())
      } else if (attempts < 20) {
        setTimeout(() => tryInit(attempts + 1), 25)
      }
    }
    setTimeout(() => tryInit(), 0)
  }

  // Ensure time auto-bump only for today; preserve chosen time for future dates
  private initDateChangeHandler(): void {
    this.dateInput = this.element.querySelector<HTMLInputElement>('input.datepicker')
    if (!this.dateInput) return

    // Capture initial time to preserve across date switches
    this.lastKnownTime = this.extractTimePart(this.dateInput.value)

    // Update cache on manual edits
    this.dateInput.addEventListener('input', () => {
      const t = this.extractTimePart(this.dateInput!.value)
      if (t) this.lastKnownTime = t
    })

    // Normalize time after date selection changes
    this.dateInput.addEventListener('change', () => {
      const raw = this.dateInput!.value || ''
      const datePart = this.extractDatePart(raw)
      if (!datePart) return

      const today = this.formatDate(new Date())
      if (datePart === today) {
        // Only bump to next top-of-hour if date is today
        const next = this.nextHourTimeString()
        this.dateInput!.value = `${datePart} ${next}`
        this.lastKnownTime = next
      } else {
        // For future dates, keep previously chosen time if available
        const keep = this.lastKnownTime || this.extractTimePart(raw)
        if (keep) this.dateInput!.value = `${datePart} ${keep}`
      }
    })
  }

  change(event?: Event): void {
    if (!this.hasLanguageSelectTarget) return
    const select = (event?.target as HTMLSelectElement) || this.languageSelectTarget
    if (!select) return
    const selected = select.value
    // Persist current locale edits before switching
    if (this.currentLocale) this.captureLocaleFromDOM(this.currentLocale)
    if (this.hasSubjectsTarget) this.toggleGroups(this.subjectsTarget, selected)
    if (this.hasBodiesTarget) this.toggleGroups(this.resolveBodiesContainer(), selected)
    // Apply stored state to newly selected locale and ensure defaults
    this.applyLocaleToDOM(selected)
    this.ensureDefaultBodyContent(selected)
    this.currentLocale = selected
    this.attachInputListeners(selected)
  }

  toggleGroups(container: HTMLElement, selected: string): void {
    const groups = Array.from(container.querySelectorAll<HTMLElement>('[data-locale]'))
    groups.forEach(group => {
      const locale = group.dataset.locale
      if (locale === selected) {
        group.style.display = ''
        group.classList.remove('notice_title__row--hidden')
      } else {
        group.style.display = 'none'
        group.classList.add('notice_title__row--hidden')
      }
    })
  }

  // --- Date/time helpers (string-based to avoid TZ drift) ---
  private extractDatePart(value: string): string | null {
    const m = value.match(/^(\w+ \d{1,2}, \d{4})/)
    return m ? m[1] : null
  }

  private extractTimePart(value: string): string | null {
    const m = value.match(/(\d{1,2}):(\d{2})\s?(AM|PM)/i)
    return m ? `${m[1]}:${m[2]} ${m[3].toUpperCase()}` : null
  }

  private nextHourTimeString(): string {
    const now = new Date()
    let hour = now.getHours() + (now.getMinutes() > 0 ? 1 : 0)
    hour = hour % 24
    const ampm = hour >= 12 ? 'PM' : 'AM'
    let h12 = hour % 12
    if (h12 === 0) h12 = 12
    return `${h12}:00 ${ampm}`
  }

  ensureDefaultBodyContent(selected: string): void {
    if (!this.hasBodiesTarget) return
    const container = this.resolveBodiesContainer()
    const activeGroup = container.querySelector<HTMLElement>(`.notice_body__locale--${selected}`)
    if (!activeGroup) return
    const frontField = (activeGroup.querySelector<HTMLTextAreaElement>('textarea[name*="[front]"]') ||
      activeGroup.querySelector<HTMLInputElement>('input[type="hidden"][name*="[front]"]'))
    if (!frontField) return
    const currentValue = (frontField as HTMLTextAreaElement | HTMLInputElement).value
    if (currentValue && currentValue.trim().length > 0) return
    const defaultFront = activeGroup.dataset.defaultFront
    if (!defaultFront) return
    ;(frontField as HTMLTextAreaElement | HTMLInputElement).value = defaultFront
    const editor = activeGroup.querySelector<HTMLElement>('[data-form-component-target="bodyEditor"]')
    if (editor && editor.innerHTML.trim().length === 0) editor.innerHTML = defaultFront
    // Store in state map so future switches retain default
    const existing = this.state.get(selected) || { subject: '', front: '' }
    this.state.set(selected, { subject: existing.subject, front: defaultFront })
  }

  // Handles nested bodies targets (outer wrapper + inner multilanguage wrapper).
  private resolveBodiesContainer(): HTMLElement {
    if (!this.hasBodiesTarget) return this.element as HTMLElement
    // Prefer the deepest bodies target that contains locale groups.
    const all = Array.from(this.element.querySelectorAll<HTMLElement>('[data-form-component-target="bodies"]'))
    const withGroups = all.reverse().find(el => el.querySelector('[data-locale]'))
    return withGroups || this.bodiesTarget
  }

  // --- State Management ---
  private initializeState(): void {
    const locales = new Set<string>()
    if (this.hasSubjectsTarget) this.subjectsTarget.querySelectorAll<HTMLElement>('[data-locale]').forEach(el => locales.add(el.dataset.locale || ''))
    if (this.hasBodiesTarget) this.resolveBodiesContainer().querySelectorAll<HTMLElement>('[data-locale]').forEach(el => locales.add(el.dataset.locale || ''))
    locales.forEach(locale => {
      if (!locale) return
      this.state.set(locale, {
        subject: this.readSubjectValue(locale),
        front: this.readFrontValue(locale)
      })
    })
    // Set initial current locale: prefer select value if target exists, else first collected locale
    const initialSelectValue = this.hasLanguageSelectTarget ? this.languageSelectTarget.value : null
    this.currentLocale = initialSelectValue || Array.from(locales)[0] || null
  }

  private readSubjectValue(locale: string): string {
    if (!this.hasSubjectsTarget) return ''
    const group = this.subjectsTarget.querySelector<HTMLElement>(`.notice_title__locale--${locale}`)
    const input = group?.querySelector<HTMLInputElement>('input[name*="[subject]"]')
    return input?.value || ''
  }

  private readFrontValue(locale: string): string {
    if (!this.hasBodiesTarget) return ''
    const group = this.resolveBodiesContainer().querySelector<HTMLElement>(`.notice_body__locale--${locale}`)
    if (!group) return ''
    const textarea = group.querySelector<HTMLTextAreaElement>('textarea[name*="[front]"]')
    const hidden = group.querySelector<HTMLInputElement>('input[type="hidden"][name*="[front]"]')
    return textarea?.value || hidden?.value || ''
  }

  private captureLocaleFromDOM(locale: string): void {
    const existing = this.state.get(locale) || { subject: '', front: '' }
    this.state.set(locale, {
      subject: this.readSubjectValue(locale) || existing.subject,
      front: this.readFrontValue(locale) || existing.front
    })
  }

  private applyLocaleToDOM(locale: string): void {
    const data = this.state.get(locale)
    if (!data) return
    if (this.hasSubjectsTarget) {
      const group = this.subjectsTarget.querySelector<HTMLElement>(`.notice_title__locale--${locale}`)
      const input = group?.querySelector<HTMLInputElement>('input[name*="[subject]"]')
      if (input && input.value !== data.subject) input.value = data.subject
    }
    if (this.hasBodiesTarget) {
      const group = this.resolveBodiesContainer().querySelector<HTMLElement>(`.notice_body__locale--${locale}`)
      if (group) {
        const textarea = group.querySelector<HTMLTextAreaElement>('textarea[name*="[front]"]')
        const hidden = group.querySelector<HTMLInputElement>('input[type="hidden"][name*="[front]"]')
        if (textarea && textarea.value !== data.front) textarea.value = data.front
        if (hidden && hidden.value !== data.front) hidden.value = data.front
        const editor = group.querySelector<HTMLElement>('[data-form-component-target="bodyEditor"]')
        if (editor && editor.innerHTML.trim() !== data.front.trim()) editor.innerHTML = data.front
      }
    }
  }

  private attachInputListeners(locale: string): void {
    // Remove previous listeners by reassigning (Stimulus will recreate on next change)
    const subjectGroup = this.hasSubjectsTarget ? this.subjectsTarget.querySelector<HTMLElement>(`.notice_title__locale--${locale}`) : null
    const bodyGroup = this.resolveBodiesContainer()?.querySelector<HTMLElement>(`.notice_body__locale--${locale}`)
    const subjectInput = subjectGroup?.querySelector<HTMLInputElement>('input[name*="[subject]"]')
    const frontTextarea = bodyGroup?.querySelector<HTMLTextAreaElement>('textarea[name*="[front]"]')
    const hiddenFront = bodyGroup?.querySelector<HTMLInputElement>('input[type="hidden"][name*="[front]"]')
    const handler = () => {
      this.state.set(locale, {
        subject: subjectInput?.value || this.state.get(locale)?.subject || '',
        front: frontTextarea?.value || hiddenFront?.value || this.state.get(locale)?.front || ''
      })
    }
    if (subjectInput) subjectInput.oninput = handler
    if (frontTextarea) frontTextarea.oninput = handler
    if (hiddenFront) hiddenFront.oninput = handler
  }

  // Called on form submission (data-action="submit->form-component#onSubmit")
  onSubmit(): void {
    if (this.currentLocale) this.captureLocaleFromDOM(this.currentLocale)
    this.state.forEach((data, locale) => {
      if (this.hasSubjectsTarget) {
        const group = this.subjectsTarget.querySelector<HTMLElement>(`.notice_title__locale--${locale}`)
        const input = group?.querySelector<HTMLInputElement>('input[name*="[subject]"]')
        if (input) input.value = data.subject
      }
      if (this.hasBodiesTarget) {
        const group = this.resolveBodiesContainer().querySelector<HTMLElement>(`.notice_body__locale--${locale}`)
        if (group) {
          const textarea = group.querySelector<HTMLTextAreaElement>('textarea[name*="[front]"]')
          const hidden = group.querySelector<HTMLInputElement>('input[type="hidden"][name*="[front]"]')
          if (textarea) textarea.value = data.front
          if (hidden) hidden.value = data.front
        }
      }
    })
  }
}
